import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';

import { Category } from '../../../models/category.model';
import {
  DurationOption,
  AggregatedSlotDto,
  CourseSessionDto,
  isBlockedCourtState,
} from '../../../service/court-availability-service';
import { TimeUtilsService } from '../../../service/time-utils.service';
import { VenueTimezoneService } from '../../../service/venue-timezone.service';
import { ToastService } from '../../../shared/services/toast.service';
import {
  leadTimeBlockedToastText,
  leadTimeTooltipText,
} from '../../../shared/utils/format-lead-time';
import { LeadTimeNoticeComponent } from '../../../shared/components/lead-time-notice/lead-time-notice.component';

/**
 * Slot status derived from court groupings.
 * Used for UI styling and slot selectability checks.
 */
export type DerivedSlotStatus =
  | 'available'
  | 'course_available'
  | 'course_full'
  | 'reserved'
  | 'blocked'
  | 'booked';

/**
 * Helper to derive slot status from court groupings.
 *
 * 'blocked' (plan §6.3.4) is checked LAST, and only when nothing else could
 * make the slot usable: a Platzsperre on court 3 while court 1 is free is an
 * *available* slot, not a closed one. It only wins when every court in the
 * breakdown is closed — then the pill says "Gesperrt" instead of "Belegt"
 * and, more importantly, refuses selection (see selectSlot()).
 */
export function deriveSlotStatus(slot: AggregatedSlotDto): DerivedSlotStatus {
  const courts = slot.courts;
  if (courts.available.length > 0) {
    return 'available';
  } else if (courts.courseJoinable.length > 0) {
    return 'course_available';
  } else if (courts.courseBlocked.length > 0) {
    return 'course_full';
  } else if (courts.reserved.length > 0) {
    return 'reserved';
  }
  const breakdown = courts.breakdown ?? [];
  if (breakdown.length > 0 && breakdown.every(court => isBlockedCourtState(court))) {
    return 'blocked';
  }
  return 'booked';
}

@Component({
  selector: 'app-start-slot-picker',
  standalone: true,
  imports: [LeadTimeNoticeComponent],
  templateUrl: './start-slot-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./start-slot-picker.component.css'],
})
export class StartSlotPickerComponent implements OnChanges, OnDestroy {
  @Input() selectedDate: Date = new Date();
  @Input() selectedCategory: Category | null = null;
  @Input() selectedDuration: DurationOption | null = null;
  @Input() loading = false;
  @Input() aggregatedSlots: AggregatedSlotDto[] = [];
  @Input() selectedSlotId: string | null = null;
  /**
   * Latest absolute UTC time a booking may start, derived from the category's
   * bookingLeadTimeMinutes. Slots beyond this are rendered disabled with a
   * German tooltip. null = no limit.
   */
  @Input() maxBookableUtc: Date | null = null;
  /** Lead time in minutes (purely for the tooltip text). */
  @Input() leadTimeMinutes: number | null = null;

  @Output() slotSelected = new EventEmitter<SlotSelectionEvent>();

  slots: AggregatedSlotDto[] = [];
  displayDate: string = '';

  errorMessage: string | null = null;
  // Internal loading state - starts as true to prevent flash of "no slots" message
  internalLoading = true;

  showEndTime = false;
  showPrice = false;
  showDuration = false;

  /** Tracks which duration option is selected for each slot */
  slotDurationSelections: Map<string, DurationOption> = new Map();

  private venueTimezone = inject(VenueTimezoneService);
  private toastService = inject(ToastService);

  constructor(private timeUtils: TimeUtilsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDate']) {
      this.updateDisplayDate();
      // Reset to loading state when date changes (new data will be fetched)
      this.internalLoading = true;
    }

    // When parent loading state changes, update internal state
    // Ignore firstChange to prevent flash of "no slots" on initial render
    if (changes['loading'] && !changes['loading'].firstChange) {
      if (this.loading) {
        // Parent started loading - show loading indicator
        this.internalLoading = true;
      } else {
        // Parent finished loading - we can now show content (or "no slots" if empty)
        this.internalLoading = false;
      }
    }

    if (changes['aggregatedSlots']) {
      this.processAvailableSlots();
    }
  }

  ngOnDestroy(): void {
    // Clear large object references
    this.slots = [];
    this.aggregatedSlots = [];
  }

  updateDisplayDate(): void {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const date = this.selectedDate;
    this.displayDate = `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  processAvailableSlots(): void {
    // isPast is now calculated by the backend using the venue timezone
    // Simply pass through the slots
    this.slots = this.aggregatedSlots;

    // If we have data, we're definitely done loading
    // If data is empty but parent is not loading, we're also done (genuinely no slots)
    // If data is empty and parent is loading, keep showing loading
    if (this.aggregatedSlots.length > 0) {
      this.internalLoading = false;
    } else if (!this.loading) {
      // Parent finished loading with empty result - show "no slots" message
      this.internalLoading = false;
    }
    // If data is empty and parent is loading, keep internalLoading as true
  }

  /**
   * Get the derived status for a slot based on court groupings
   */
  getSlotStatus(slot: AggregatedSlotDto): DerivedSlotStatus {
    return deriveSlotStatus(slot);
  }

  /**
   * True when the slot's start time is later than the category's
   * configured booking window (now + bookingLeadTimeMinutes).
   */
  isOutOfLeadTime(slot: AggregatedSlotDto): boolean {
    if (!this.maxBookableUtc) return false;
    const start = new Date(slot.startTimeUtc).getTime();
    return start > this.maxBookableUtc.getTime();
  }

  leadTimeTooltip(): string {
    return this.leadTimeMinutes == null
      ? ''
      : leadTimeTooltipText(this.leadTimeMinutes);
  }

  /** Shown via toast when the user clicks a slot beyond the lead-time window. */
  private notifyOutOfLeadTime(): void {
    if (this.leadTimeMinutes == null) return;
    this.toastService.info(leadTimeBlockedToastText(this.leadTimeMinutes));
  }

  timeStringToMinutes(timeString: string): number {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  /**
   * Handles selection of a specific slot with a specific duration
   * @param slot - The time slot being selected
   * @param duration - The specific duration option being selected for this slot
   */
  selectSlotWithDuration(slot: AggregatedSlotDto, duration: DurationOption): void {
    // Don't allow selection of past slots
    if (slot.isPast) {
      return;
    }
    // Block selection of slots beyond the category's lead-time window.
    // The backend re-validates on POST /holds; this just avoids a wasted round-trip.
    if (this.isOutOfLeadTime(slot)) {
      this.notifyOutOfLeadTime();
      return;
    }

    if (this.getSlotStatus(slot) === 'available') {
      // Save the selected duration for this slot
      this.slotDurationSelections.set(slot.startTimeUtc, duration);

      // Emit using the interface type
      this.slotSelected.emit({
        slot,
        selectedDuration: duration
      });
    }
  }

  /**
   * Gets the total available spots for joinable courses in a slot
   * @param slot - The court slot to check
   * @returns The total number of available spots across all joinable courses
   */
  getJoinableCourseSpots(slot: AggregatedSlotDto): number {
    if (!slot.courses?.joinable?.length) return 0;
    return slot.courses.joinable
      .reduce((sum: number, session: CourseSessionDto) => sum + session.availableSpots, 0);
  }

  /**
   * Handles selection of a slot without a specific duration
   * @param slot - The time slot being selected
   */
  selectSlot(slot: AggregatedSlotDto): void {
    // Don't allow selection of past slots
    if (slot.isPast) {
      return;
    }
    // Block slots beyond the category's lead-time window.
    if (this.isOutOfLeadTime(slot)) {
      this.notifyOutOfLeadTime();
      return;
    }

    const status = this.getSlotStatus(slot);
    // Every court closed (Platzsperre / hidden occupancy, plan §6.3.4): there
    // is nothing to book and nothing to join. Refused explicitly rather than
    // relying on the group arrays below happening to be empty — a closed
    // court must never become selectable by accident.
    if (status === 'blocked') {
      return;
    }
    const hasAvailableCourts = slot.courts.available.length > 0;
    const hasJoinableCourses = (slot.courses?.joinable?.length ?? 0) > 0;
    const hasBlockedCourses = (slot.courses?.full?.length ?? 0) > 0 ||
                              (slot.courses?.requiresApproval?.length ?? 0) > 0 ||
                              (slot.courses?.inviteOnly?.length ?? 0) > 0 ||
                              (slot.courses?.membersOnly?.length ?? 0) > 0;

    // Check if slot is selectable (has available courts or has course sessions - including full ones)
    const isSelectable =
      status === 'available' ||
      status === 'course_available' ||
      status === 'course_full' ||
      hasAvailableCourts ||
      hasJoinableCourses ||
      hasBlockedCourses;

    if (!isSelectable) {
      // Slot is not selectable (e.g., fully booked with no courses)
      return;
    }

    // Clear any previously selected duration for this slot
    this.slotDurationSelections.delete(slot.startTimeUtc);

    // Always emit slotSelected - the booking container will handle
    // showing the course-picker if courses are available
    this.slotSelected.emit({
      slot,
      selectedDuration: undefined // No specific duration selected
    });
  }

  /**
   * Determines if a specific slot and duration combination is currently selected
   * @param slot - The time slot to check
   * @param duration - Optional duration to check (if provided)
   * @returns True if this specific slot and duration combination is selected
   */
  isSelected(slot: AggregatedSlotDto, duration?: DurationOption): boolean {
    const isSlotSelected = this.selectedSlotId === slot.startTimeUtc;

    if (!duration) {
      // Just check if the slot is selected
      return isSlotSelected;
    }

    // Check if this specific duration is selected for this slot
    const selectedDuration = this.slotDurationSelections.get(slot.startTimeUtc);
    return isSlotSelected && !!selectedDuration && selectedDuration.duration === duration.duration;
  }

  /**
   * Determines if a slot should display mixed-status styling.
   * A slot has "mixed status" when it has both available courts for regular booking
   * AND course sessions that can be joined. This styling helps users understand
   * they have multiple booking options.
   *
   * @param slot - The court slot to check
   * @returns True if slot should show mixed-status styling (has both options AND is selectable/not past)
   */
  shouldShowMixedStatusStyling(slot: AggregatedSlotDto): boolean {
    // Don't show mixed status for past slots (they're not selectable)
    if (slot.isPast) {
      return false;
    }

    const hasAvailableCourts = slot.courts.available.length > 0;
    const hasJoinableCourses = (slot.courses?.joinable?.length ?? 0) > 0;
    const hasBlockedCourses = (slot.courses?.full?.length ?? 0) > 0 ||
                              (slot.courses?.requiresApproval?.length ?? 0) > 0 ||
                              (slot.courses?.inviteOnly?.length ?? 0) > 0 ||
                              (slot.courses?.membersOnly?.length ?? 0) > 0;

    return hasAvailableCourts && (hasJoinableCourses || hasBlockedCourses);
  }

  /**
   * German tooltip for pills that carry a course option (design nice-to-have).
   * Wording follows actual joinability — "Kurs verfügbar" only when a course
   * can really be joined, "Kurs ausgebucht" for full-only courses, and
   * "Kurs nicht verfügbar" when access is restricted (approval/invite/members).
   * Past pills keep no tooltip, and out-of-lead-time pills keep the lead-time
   * tooltip (handled in template).
   */
  courseTooltip(slot: AggregatedSlotDto): string | null {
    if (slot.isPast) {
      return null;
    }
    // Every court closed (plan §6.3.4) — say so instead of leaving the pill
    // silent, and before the course wording: there is no course here to talk about.
    if (this.getSlotStatus(slot) === 'blocked') {
      return 'Plätze gesperrt';
    }
    if ((slot.courses?.joinable?.length ?? 0) > 0) {
      return 'Kurs verfügbar';
    }
    const fullCount = slot.courses?.full?.length ?? 0;
    const restrictedCount =
      (slot.courses?.requiresApproval?.length ?? 0) +
      (slot.courses?.inviteOnly?.length ?? 0) +
      (slot.courses?.membersOnly?.length ?? 0);
    if (fullCount > 0 && restrictedCount === 0) {
      return 'Kurs ausgebucht';
    }
    if (restrictedCount > 0) {
      return 'Kurs nicht verfügbar';
    }
    return null;
  }

  /**
   * Format time for display using venue timezone
   * @param timeString - Either an ISO instant (with Z) or a simple time string (HH:mm)
   * @returns Formatted time string in venue timezone
   */
  formatTime(timeString: string): string {
    if (!timeString) return '';

    try {
      // If it's an ISO instant, use VenueTimezoneService for proper timezone conversion
      if (this.venueTimezone.isValidIsoInstant(timeString)) {
        return this.venueTimezone.formatTime(timeString);
      }

      // If it's already a simple time string like "14:00", just return it
      if (timeString.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
        return timeString.substring(0, 5); // Just take HH:MM
      }

      // Fallback
      return timeString;
    } catch (e) {
      console.warn('Error formatting time:', e);
      return timeString;
    }
  }
}

// Interface for slot selection events
export interface SlotSelectionEvent {
  slot: AggregatedSlotDto;
  selectedDuration?: DurationOption;
}

// Interface for course session click events
export interface CourseSessionClickEvent {
  slot: AggregatedSlotDto;
  courseSessions: CourseSessionDto[];
}
